import { SetStateAction, useCallback, useMemo, useState } from 'react';

import { GridPaginationModel } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';

import { queryParameters } from '~constants';

/**
 * A hook to use pagination that keeps its state within the url
 * Notice that the first page for the grid = 0, but the first page
 * for the url = 1
 * @param defaultPageSize
 * @param disablePageUrlSync A boolean to turn off synchronization with the url
 * @returns
 */
const useUrlSearchParamsPagination = (
	defaultPageSize = 10,
	disablePageUrlSync = false,
	disablePageSizeUrlSync = false
): [GridPaginationModel, (model: SetStateAction<GridPaginationModel>) => void] => {
	const [searchParams, setSearchParams] = useSearchParams();

	const pageFromUrl = Number(searchParams.get(queryParameters.PageNumber) ?? '0');
	const pageSizeFromUrl = Number(searchParams.get(queryParameters.PageSize));

	const [internalPage, setInternalPage] = useState(0);
	const [internalPageSize, setInternalPageSize] = useState(defaultPageSize);

	/**
	 * The pagination model that is synced with the url
	 */
	const paginationModel = useMemo<GridPaginationModel>(() => {
		const page =
			disablePageUrlSync ? internalPage
			: pageFromUrl > 1 ? pageFromUrl - 1
			: 0;
		const pageSize =
			disablePageSizeUrlSync ? internalPageSize
			: pageSizeFromUrl > 0 ? pageSizeFromUrl
			: defaultPageSize;

		return {
			page: page,
			pageSize: pageSize,
		};
	}, [
		pageFromUrl,
		pageSizeFromUrl,
		internalPage,
		internalPageSize,
		defaultPageSize,
		disablePageUrlSync,
		disablePageSizeUrlSync,
	]);

	const setPaginationModel = useCallback(
		(modelNew: SetStateAction<GridPaginationModel>) => {
			const model =
				typeof modelNew === 'function' ?
					modelNew({ page: paginationModel.page, pageSize: paginationModel.pageSize })
				:	modelNew;

			if (disablePageUrlSync) {
				setInternalPage(model.page);
			}
			if (disablePageSizeUrlSync) {
				setInternalPageSize(model.pageSize);
			}

			if (!disablePageUrlSync || !disablePageSizeUrlSync) {
				setSearchParams(
					(prev) => {
						if (!disablePageUrlSync && model.page > 0) {
							prev.set(queryParameters.PageNumber, (model.page + 1).toString());
						} else if (!disablePageUrlSync) {
							prev.delete(queryParameters.PageNumber);
						}

						if (!disablePageSizeUrlSync && model.pageSize > 0) {
							prev.set(queryParameters.PageSize, model.pageSize.toString());
						}

						return prev;
					},
					{ replace: true }
				);
			}
		},
		[disablePageSizeUrlSync, disablePageUrlSync, paginationModel, setSearchParams]
	);

	return [paginationModel, setPaginationModel];
};

export default useUrlSearchParamsPagination;
